import { z } from 'zod';

import { currencySchema } from './currency';
import { imageRefEntitySchema, imageRefWireSchema } from './image';
import { isoDateTimeSchema, minorUnitsBigintSchema, ulidSchema } from './primitives';

/**
 * Per-language overrides for text fields. Keys are BCP-47 language codes (e.g. `'pap'`, `'nl'`).
 * English is always the primary/fallback; only alternate-language overrides are stored here.
 */
export const menuItemTranslationSchema = z.object({
  name: z.string().min(1).max(140).optional(),
  description: z.string().max(2000).optional(),
});
export type MenuItemTranslation = z.infer<typeof menuItemTranslationSchema>;

export const menuItemTranslationsSchema = z.record(z.string(), menuItemTranslationSchema);
export type MenuItemTranslations = z.infer<typeof menuItemTranslationsSchema>;

export const menuItemEntitySchema = z.object({
  menuItemId: ulidSchema,
  restaurantId: ulidSchema,
  name: z.string().min(1).max(140),
  description: z.string().max(2000).optional(),
  /** Legacy free-text label; prefer `menuCategoryId` for structured menus. */
  category: z.string().max(80).optional(),
  menuCategoryId: ulidSchema.optional(),
  sortOrder: z.number().int().nonnegative().default(0),
  priceMinorUnits: minorUnitsBigintSchema,
  currency: currencySchema,
  available: z.boolean().default(true),
  image: imageRefEntitySchema.optional(),
  translations: menuItemTranslationsSchema.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type MenuItemEntity = z.infer<typeof menuItemEntitySchema>;

export const menuItemPublicSchema = z.object({
  menuItemId: ulidSchema,
  name: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  menuCategoryId: ulidSchema.optional(),
  sortOrder: z.number().int().nonnegative(),
  priceMinorUnits: z.string().describe('bigint serialized as string'),
  currency: currencySchema,
  available: z.boolean(),
  image: imageRefWireSchema.optional(),
  translations: menuItemTranslationsSchema.optional(),
});
export type MenuItemPublic = z.infer<typeof menuItemPublicSchema>;

export const menuItemOwnerSchema = menuItemPublicSchema.extend({
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type MenuItemOwner = z.infer<typeof menuItemOwnerSchema>;
